import {
  StackProps,
  VStack,
  SkeletonCircle,
  Avatar,
  Skeleton,
  Circle,
  Box,
  Image,
  Heading,
  HStack,
  Tooltip,
} from '@chakra-ui/react';
import { AvatarCustomBadge } from 'Molecules/AvatarCustomBadge';
import { Header, LabelText, Typography } from 'Tokens';
import { TruncatableText } from 'Atoms';
import { CompanyIcon } from 'Tokens/Icons/Data';
import { CompanyDetails } from 'models';

export const CompanyLogo = ({
  url,
  size,
  borderRadius,
}: {
  url: string;
  size: string;
  shadow?: string;
  borderRadius?: string;
}) => {
  if (borderRadius) {
    return (
      <Box overflow="hidden" bg="bg.unknown" width={size} height={size} borderRadius={borderRadius}>
        <Image src={url} width={size} height={size} objectFit="cover" />
      </Box>
    );
  }

  return (
    <Circle overflow="hidden" bg="bg.unknown" size={size}>
      <Image src={url} width={size} height={size} />
    </Circle>
  );
};

export const CompanyPresentation = ({
  company,
  isSelected,
  logoSize,
  logoBorderRadius,
}: {
  company: Pick<CompanyDetails, 'logoUrl' | 'name'>;
  isSelected?: boolean;
  logoSize?: string;
  logoBorderRadius?: string;
}) => {
  return (
    <HStack alignItems="center">
      {company.logoUrl ? (
        <CompanyLogo
          url={company.logoUrl}
          size={logoSize || '24px'}
          borderRadius={logoBorderRadius}
        />
      ) : (
        <CompanyIcon
          color="text.onAccent"
          bgColor="bg.accent"
          padding="6px"
          w={logoSize || '28px'}
          h={logoSize || '28px'}
          borderRadius={logoBorderRadius || '6px'}
        />
      )}
      <VStack alignItems="flex-start" spacing="0px">
        <Tooltip label={company.name} placement="bottom-start">
          <TruncatableText
            text={company.name}
            variant="bodyStrong"
            color={isSelected ? 'text.selected' : 'text.default'}
            width="180px"
          />
        </Tooltip>
        {isSelected && (
          <Typography variant="detail" color="text.selected">
            You are here
          </Typography>
        )}
      </VStack>
    </HStack>
  );
};

export const CompanyAvatar = ({
  company,
  ...rest
}: StackProps & { company?: Pick<CompanyDetails, 'name' | 'logoUrl'> }) => (
  <VStack {...rest}>
    <SkeletonCircle isLoaded={!!company} size="64px">
      {company?.logoUrl ? (
        <CompanyLogo url={company.logoUrl} size="64px" />
      ) : (
        <Avatar size="lg" name={company?.name} src={company?.logoUrl ?? undefined} />
      )}
    </SkeletonCircle>
    <Skeleton isLoaded={!!company}>
      <Header size="lg">{company?.name}</Header>
    </Skeleton>
  </VStack>
);

export const CompanyAvatarWithName = ({
  company,
  hideName = false,
  status,
  isRounded = true,
}: {
  company?: Pick<CompanyDetails, 'name' | 'logoUrl'>;
  hideName?: boolean;
  status?: string;
  isRounded?: boolean;
}) => (
  <HStack>
    {company?.logoUrl ? (
      <Tooltip label={company?.name} color="text.onAccent">
        <CompanyLogo url={company.logoUrl} size="32px" />
      </Tooltip>
    ) : (
      <Tooltip label={`${company?.name}${status ? ': ' + status : ''}`} color="text.onAccent">
        <Avatar
          size="sm"
          name={company?.name}
          src={company?.logoUrl ?? undefined}
          borderRadius={isRounded ? '50%' : '6px'}
        >
          {status && <AvatarCustomBadge status={status} />}
        </Avatar>
      </Tooltip>
    )}
    {!hideName && <LabelText fontSize="md">{company?.name}</LabelText>}
  </HStack>
);
